import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, Spinner, Alert, Nav, Tab, Badge, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  KPICard,
  EarningsFilters,
  LineChart,
  BarChart,
  PieChart,
  HeatmapChart
} from 'components/analytics';
import { api } from 'api';
import { formatCurrency, formatDate } from 'common/utils';
import 'pages/analytics/_analytics.scss';
import { CURRENCY } from 'common/constants';
import {
  FaSync,
  FaDownload,
  FaChartLine,
  FaDollarSign,
  FaBuilding,
  FaClipboardList,
  FaExclamationTriangle,
  FaCheckCircle,
  FaInfoCircle,
  FaUserFriends,
  FaClock,
  FaCalendarCheck,
  FaMapMarkerAlt,
  FaUsers
} from 'react-icons/fa';

const Earnings = () => {
  // Helper function to format relative time
  const getRelativeTime = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    }
  };

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    ground_id: ''
  });

  const [data, setData] = useState({
    earningsSummary: null,
    bookingAnalytics: null,
    revenueBySport: [],
    bookingHeatmap: [],
    dailySlotOccupancy: [],
  });

  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [dataStatus, setDataStatus] = useState('loading'); // loading, loaded, error, empty

  const loadEarningsData = useCallback(async (isRefresh = false, customFilters = null) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    setDataStatus('loading');

    try {
      // Use custom filters if provided, otherwise use current state
      const currentFilters = customFilters || filters;
      const params = {
        ...currentFilters,
        start_date: currentFilters.start_date || undefined,
        end_date: currentFilters.end_date || undefined,
        ground_id: currentFilters.ground_id || undefined
      };

      // Remove empty values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === undefined) {
          delete params[key];
        }
      });

      const [
        summaryRes,
        analyticsRes,
        sportRes,
        heatmapRes,
        occupancyRes
      ] = await Promise.all([
        api.getGroundAdminEarningsSummary(params),
        api.getGroundAdminBookingAnalytics(params),
        api.getGroundAdminRevenueBySport(params),
        api.getGroundAdminBookingHeatmap(params),
        api.getGroundAdminDailySlotOccupancy(params),
      ]);

      // Process earnings summary data
      const processedEarningsSummary = summaryRes ? {
        ...summaryRes,
        total_earnings: parseFloat(summaryRes.total_earnings) || 0,
        withdrawable_balance: parseFloat(summaryRes.withdrawable_balance) || 0,
        pending_withdrawals: parseFloat(summaryRes.pending_withdrawals) || 0,
      } : null;

      // Process booking analytics data
      const processedBookingAnalytics = analyticsRes ? {
        ...analyticsRes,
        cancellation_rate: parseFloat(analyticsRes.cancellation_rate) || 0,
        slot_occupancy_rate: parseFloat(analyticsRes.slot_occupancy_rate) || 0
      } : null;

      // Process revenue by sport data
      const processedRevenueBySport = (sportRes || []).map(item => ({
        ...item,
        revenue: parseFloat(item.revenue) || 0,
        percentage: parseFloat(item.percentage) || 0
      }));

      // Process booking trends data
      const processedBookingTrends = (analyticsRes?.booking_trends || []).map(item => ({
        ...item,
        revenue: parseFloat(item.revenue) || 0
      }));

      const hasData = processedEarningsSummary || processedBookingAnalytics ||
        processedRevenueBySport.length > 0;

      setData({
        earningsSummary: processedEarningsSummary,
        bookingAnalytics: processedBookingAnalytics,
        revenueBySport: processedRevenueBySport,
        bookingHeatmap: heatmapRes || [],
        bookingTrends: processedBookingTrends,
        dailySlotOccupancy: occupancyRes || []
      });

      setDataStatus(hasData ? 'loaded' : 'empty');
      setLastUpdated(new Date());

      if (isRefresh) {
        toast.success('Data refreshed successfully');
      }
    } catch (error) {
      console.error('Error loading earnings data:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load earnings data. Please try again.';
      setError(errorMessage);
      setDataStatus('error');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setInitialLoading(false);
    }
  }, []); // Remove filters dependency to prevent automatic reloads

  useEffect(() => {
    loadEarningsData();
  }, []); // Only load on initial mount

  // Remove auto-refresh as it's not mentioned in user story

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    // Trigger data reload with new filters
    loadEarningsData(false, newFilters);
  };

  const handleExport = async (type, format = 'csv') => {
    try {
      setExportLoading(true);
      const params = {
        type,
        format,
        ...filters,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        ground_id: filters.ground_id || undefined
      };

      // Remove empty values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await api.exportGroundAdminData(params);

      // Create download link
      let mimeType, fileExtension;
      switch (format) {
        case 'csv':
          mimeType = 'text/csv';
          fileExtension = 'csv';
          break;
        case 'json':
          mimeType = 'application/json';
          fileExtension = 'json';
          break;
        case 'pdf':
          mimeType = 'application/pdf';
          fileExtension = 'pdf';
          break;
        default:
          mimeType = 'text/csv';
          fileExtension = 'csv';
      }

      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `earnings_${type}_${new Date().toISOString().split('T')[0]}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} exported successfully`);
    } catch (error) {
      console.error('Error exporting data:', error);
      const errorMessage = error.response?.data?.message || 'Failed to export data';
      toast.error(errorMessage);
    } finally {
      setExportLoading(false);
    }
  };

  const handleRefresh = () => {
    loadEarningsData(true);
  };

  const handleKPIClick = (kpiType) => {
    // Navigate to appropriate tab based on KPI type
    switch (kpiType) {
      case 'earnings':
      case 'withdrawable':
        setActiveTab('overview');
        break;
      case 'bookings':
        setActiveTab('bookings');
        break;
      default:
        setActiveTab('overview');
    }
  };


  if (error) {
    return (
      <Container fluid className="analytics-page">
        <Alert variant="danger" className="mt-4">
          {error}
          <Button
            variant="outline-danger"
            size="sm"
            className="ms-2"
            onClick={loadEarningsData}
          >
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="analytics-page">
      <div className="analytics-header">
        <div>
          <div className="d-flex align-items-center gap-3 mb-3">
            <h2 className="mb-0">Earnings & Analytics Dashboard</h2>
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip>Refresh data</Tooltip>}
            >
              <Button
                variant="outline-secondary"
                size="lg"
                onClick={handleRefresh}
                disabled={loading || refreshing}
                className="d-flex align-items-center gap-2"
              >
                {refreshing ? (
                  <Spinner size="sm"/>
                ) : (
                  <FaSync className={refreshing ? 'fa-spin' : ''}/>
                )}
              </Button>
            </OverlayTrigger>
          </div>
          <div className="d-flex align-items-center justify-content-center gap-2">
            {lastUpdated && (
              <small className="text-muted">
                <FaClock className="last-updated-icon"/>
                Last updated: {getRelativeTime(lastUpdated)}
              </small>
            )}
            {dataStatus === 'loaded' && (
              <Badge bg="success" className="d-flex align-items-center gap-1">
                <FaCheckCircle size={12}/>
                Data Loaded
              </Badge>
            )}
            {dataStatus === 'error' && (
              <Badge bg="danger" className="d-flex align-items-center gap-1">
                <FaExclamationTriangle size={12}/>
                Error
              </Badge>
            )}
            {dataStatus === 'empty' && (
              <Badge bg="warning" className="d-flex align-items-center gap-1">
                <FaInfoCircle size={12}/>
                No Data
              </Badge>
            )}
          </div>
        </div>
      </div>


      <EarningsFilters
        onFiltersChange={handleFiltersChange}
        loading={loading}
      />

      {initialLoading && (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" size="lg">
            <span className="visually-hidden">Loading earnings data...</span>
          </Spinner>
          <p className="mt-3 text-muted">Loading earnings data...</p>
        </div>
      )}

      {dataStatus === 'empty' && !initialLoading && (
        <div className="text-center py-5">
          <div className="mb-4">
            <FaDollarSign size={64} className="text-muted"/>
          </div>
          <h4 className="text-muted">No Data Available</h4>
          <p className="text-muted">No earnings data found for the selected filters.</p>
          <Button
            variant="primary"
            onClick={handleRefresh}
            className="mt-3"
          >
            <FaSync className="me-2"/>
            Refresh Data
          </Button>
        </div>
      )}

      {dataStatus === 'loaded' && data.earningsSummary && (
        <div className="analytics-tabs-container">
          <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
            <Row>
              <Col lg={12}>
                <Nav variant="tabs" className="analytics-tabs">
                  <Nav.Item>
                    <Nav.Link eventKey="overview" className="analytics-tab">
                      <FaChartLine className="me-2"/>
                      Earnings Overview
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="analytics" className="analytics-tab">
                      <FaCalendarCheck className="me-2"/>
                      Booking Analytics
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
            </Row>

            <Tab.Content>
              <Tab.Pane eventKey="overview">
                <EarningsOverviewTab
                  earningsSummary={data.earningsSummary}
                  bookingAnalytics={data.bookingAnalytics}
                  revenueBySport={data.revenueBySport}
                  onKPIClick={handleKPIClick}
                />
              </Tab.Pane>
              <Tab.Pane eventKey="analytics">
                <EarningsAnalyticsTab
                  bookingAnalytics={data.bookingAnalytics}
                  bookingHeatmap={data.bookingHeatmap}
                  bookingTrends={data.bookingTrends}
                  dailySlotOccupancy={data.dailySlotOccupancy}
                />
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </div>
      )}

    </Container>
  );
};

// Earnings Overview Tab Component
const EarningsOverviewTab = ({ earningsSummary, bookingAnalytics, revenueBySport, onKPIClick }) => {
  if (!earningsSummary) return null;

  return (
    <>
      {/* Earnings Summary KPIs */}
      <Row className="g-4 mb-4">
        <Col md={6}>
          <KPICard
            title="Total Earnings"
            value={`${CURRENCY} ${earningsSummary.total_earnings.toLocaleString()}`}
            subtitle="All time earnings"
            color="primary"
            icon={<FaDollarSign/>}
            onClick={() => onKPIClick('earnings')}
          />
        </Col>
        {/*<Col md={4}>*/}
        {/*  <KPICard*/}
        {/*    title="Withdrawable Balance"*/}
        {/*    value={`${CURRENCY} ${earningsSummary.withdrawable_balance.toLocaleString()}`}*/}
        {/*    subtitle={`${CURRENCY} ${earningsSummary.pending_withdrawals.toLocaleString()} pending`}*/}
        {/*    color="success"*/}
        {/*    icon={<FaBuilding/>}*/}
        {/*    onClick={() => onKPIClick('withdrawable')}*/}
        {/*  />*/}
        {/*</Col>*/}
      </Row>
      {earningsSummary.platform_fee_percentage != null && (
        <p className="text-muted small mb-3">
          Platform fee: {Number(earningsSummary.platform_fee_percentage)}% (from global settings)
        </p>
      )}

      {/* Grounds Breakdown */}
      {earningsSummary.grounds_breakdown && earningsSummary.grounds_breakdown.length > 0 && (
        <Row className="g-4 mb-4">
          <Col lg={12}>
            <BarChart
              data={earningsSummary.grounds_breakdown.map(ground => ({
                category: ground.ground_name,
                amount: parseFloat(ground.earnings) || 0,
                bookings: ground.bookings_count || 0
              }))}
              title="Earnings by Ground (PKR)"
              dataKey="amount"
              xAxisKey="category"
              color="#28a745"
              height={500}
            />
          </Col>
        </Row>
      )}

      {/* Revenue by Sport */}
      {revenueBySport && revenueBySport.length > 0 && (
        <Row className="g-4 mb-4">
          <Col lg={12}>
            <BarChart
              data={revenueBySport.map(sport => ({
                category: sport.sport_name,
                amount: parseFloat(sport.revenue) || 0,
                percentage: parseFloat(sport.percentage) || 0
              }))}
              title="Revenue by Sport (PKR)"
              dataKey="amount"
              xAxisKey="category"
              color="#007bff"
              height={500}
            />
          </Col>
        </Row>
      )}
    </>
  );
};

// Earnings Analytics Tab Component
const EarningsAnalyticsTab = ({ bookingAnalytics, bookingHeatmap, bookingTrends, dailySlotOccupancy }) => {
  return (
    <>
      {/* Booking Analytics KPIs */}
      {bookingAnalytics && (
        <Row className="g-4 mb-4">
          <Col md={3}>
            <KPICard
              title="Total Bookings"
              value={bookingAnalytics.total_bookings || 0}
              subtitle="All bookings"
              color="primary"
              icon={<FaClipboardList/>}
            />
          </Col>
          <Col md={3}>
            <KPICard
              title="Confirmed"
              value={bookingAnalytics.confirmed_bookings || 0}
              subtitle={`${bookingAnalytics.completed_bookings || 0} completed`}
              color="success"
              icon={<FaCheckCircle/>}
            />
          </Col>
          <Col md={3}>
            <KPICard
              title="Cancelled"
              value={bookingAnalytics.cancelled_bookings || 0}
              subtitle={`${bookingAnalytics.cancellation_rate || 0}% rate`}
              color="danger"
              icon={<FaExclamationTriangle/>}
            />
          </Col>
        </Row>
      )}

      {/* Booking Trends */}
      {bookingTrends && bookingTrends.length > 0 && (
        <Row className="g-4 mb-4">
          <Col lg={6}>
            <LineChart
              data={bookingTrends}
              title="Booking Trends Over Time"
              dataKey="bookings_count"
              xAxisKey="date"
              color="#007bff"
              height={350}
            />
          </Col>
          <Col lg={6}>
            <LineChart
              data={bookingTrends}
              title="Revenue Trends Over Time"
              dataKey="revenue"
              xAxisKey="date"
              color="#28a745"
              height={350}
            />
          </Col>
        </Row>
      )}

      {/* Peak Hours and Days */}
      {bookingAnalytics && (bookingAnalytics.peak_hours || bookingAnalytics.peak_days) && (
        <Row className="g-4 mb-4">
          {bookingAnalytics.peak_hours && bookingAnalytics.peak_hours.length > 0 && (
            <Col lg={6}>
              <BarChart
                data={bookingAnalytics.peak_hours.map(hour => ({
                  category: hour.hour_display,
                  amount: hour.bookings_count,
                  hour: hour.hour
                }))}
                title="Peak Hours"
                dataKey="amount"
                xAxisKey="category"
                color="#28a745"
                height={350}
              />
            </Col>
          )}
          {bookingAnalytics.peak_days && bookingAnalytics.peak_days.length > 0 && (
            <Col lg={6}>
              <BarChart
                data={bookingAnalytics.peak_days.map(day => ({
                  category: day.day_name,
                  amount: day.bookings_count,
                  day_of_week: day.day_of_week
                }))}
                title="Peak Days"
                dataKey="amount"
                xAxisKey="category"
                color="#17a2b8"
                height={350}
              />
            </Col>
          )}
        </Row>
      )}

      {/* Daily Slot Occupancy */}
      {dailySlotOccupancy && dailySlotOccupancy.length > 0 && (
        <Row className="g-4 mb-4">
          <Col lg={12}>
            <BarChart
              data={dailySlotOccupancy.map(day => ({
                category: new Date(day.date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                }),
                amount: day.occupancy_rate,
                total_slots: day.total_slots,
                booked_slots: day.booked_slots,
                available_slots: day.available_slots
              }))}
              title="Daily Slot Occupancy Rate (%)"
              dataKey="amount"
              xAxisKey="category"
              color="#ffc107"
              height={400}
            />
          </Col>
        </Row>
      )}

      {/* Booking Heatmap */}
      {bookingHeatmap && bookingHeatmap.length > 0 && (
        <Row className="g-4">
          <Col lg={12}>
            <HeatmapChart
              data={bookingHeatmap}
              title="Booking Activity Heatmap"
              height={400}
            />
          </Col>
        </Row>
      )}
    </>
  );
};


export default Earnings;